import { Satellite, Wifi, Activity, HardDrive, Router, Loader2, CheckCircle2, AlertCircle, Signal, ArrowDown, ArrowUp, Zap, TrendingDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { StarlinkSystem } from '@/types';
import { authenticatedFetch } from '@/lib/api';
import { sanitizeSeries } from '@/lib/chartData';
import { formatNumber } from '@/lib/utils';
import { connectionStatusVariant } from '@/lib/connectivityStatus';
import { InfoTooltip } from '@/components/ui/info-tooltip';

interface StarlinkPanelProps {
  system: StarlinkSystem;
  unitId: string;
  onUpdate?: (settings: Partial<StarlinkSystem>) => void;
  onReboot?: (systemId: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

// Connection-status badge colours: src/lib/connectivityStatus (ISS-021/022) —
// the single source that now covers suspended/degraded/unknown/not_activated too.

export function StarlinkPanel({ system, unitId, onUpdate: _onUpdate, onReboot, onRefresh }: StarlinkPanelProps) {
  const [rebootState, setRebootState] = useState<{
    loading: boolean;
    error: string | null;
    success: boolean;
  }>({
    loading: false,
    error: null,
    success: false,
  });

  // Refs to store timer IDs for cleanup
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Missing telemetry stays missing.
  //
  // These used to default to -70 dBm / 75% / -95 dBm and zeroed metrics, which
  // re-introduced exactly the fabrication the API stopped emitting: the backend
  // now sends null for "never measured", and converting that back to a
  // plausible reading here would defeat the point. Absence renders as N/A.
  const connectionStatus = system.connectionStatus || 'unknown';
  const signalStrength = system.signalStrength ?? null;
  const signalQuality = system.signalQuality ?? null;
  const noiseLevel = system.noiseLevel ?? null;
  const currentMetrics = system.currentMetrics || {
    downloadSpeed: null,
    uploadSpeed: null,
    pingLatency: null,
    packetLoss: null,
    timestamp: new Date().toISOString(),
  };
  const metricsHistory = system.metricsHistory?.length ? system.metricsHistory : [];
  const wifiClients = system.wifiClients || [];
  const clients2_4GHz = system.clients2_4GHz ?? 0;
  const clients5GHz = system.clients5GHz ?? 0;
  const totalClients = system.totalClients ?? (clients2_4GHz + clients5GHz);
  const terminalId = system.terminalId || 'Unknown';
  const firmwareVersion = system.firmwareVersion || 'N/A';
  /** Renders a measurement, or N/A when it was never taken. */
  const measured = (value: number | null | undefined, decimals = 1, suffix = '') =>
    value == null ? 'N/A' : `${formatNumber(value, decimals)}${suffix}`;
  const hardwareVersion = system.hardwareVersion || 'N/A';
  const uptime = system.uptime ?? null;
  const ssid = system.ssid || 'Unknown';

  // Cleanup timers on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const handleReboot = async () => {
    // Reset state
    setRebootState({ loading: true, error: null, success: false });

    try {
      // Use callback if provided, otherwise call API directly
      if (onReboot) {
        await onReboot(terminalId);
      } else {
        const response = await authenticatedFetch(`/api/v1/units/${unitId}/starlink/reboot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to reboot terminal' }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Reboot failed');
        }
      }

      // Success - show feedback and refresh data
      setRebootState({ loading: false, error: null, success: true });

      // Refresh Starlink data after a short delay to allow reboot to process
      if (onRefresh) {
        refreshTimerRef.current = setTimeout(() => {
          onRefresh();
        }, 2000);
      }

      // Clear success message after 3 seconds
      successTimerRef.current = setTimeout(() => {
        setRebootState({ loading: false, error: null, success: false });
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reboot terminal';
      setRebootState({ loading: false, error: errorMessage, success: false });

      // Clear error message after 5 seconds
      errorTimerRef.current = setTimeout(() => {
        setRebootState((prev) => ({ ...prev, error: null }));
      }, 5000);
    }
  };
  // Prepare data for WiFi client distribution chart
  const _wifiDistribution = [
    { name: '2.4 GHz', value: clients2_4GHz, fill: '#3B82F6' },
    { name: '5 GHz', value: clients5GHz, fill: '#10B981' }
  ];

  // Prepare channel distribution data
  const channelData = wifiClients.reduce((acc, client) => {
    const key = `Ch ${client.channel} (${client.band})`;
    if (!acc[key]) {
      acc[key] = { channel: key, count: 0, band: client.band };
    }
    acc[key].count++;
    return acc;
  }, {} as Record<string, { channel: string; count: number; band: string }>);

  const channelDistribution = Object.values(channelData).sort((a, b) => {
    // Sort by band first (2.4GHz before 5GHz), then by channel
    if (a.band !== b.band) {
      return a.band === '2.4GHz' ? -1 : 1;
    }
    // Extract channel numbers and compare numerically
    const channelA = parseInt(a.channel.match(/\d+/)?.[0] || '0', 10);
    const channelB = parseInt(b.channel.match(/\d+/)?.[0] || '0', 10);
    return channelA - channelB;
  });

  // Format metrics history for ApexCharts (last 24 data points)
  const historySlice = metricsHistory.slice(-24);
  const timeLabels = historySlice.length > 0
    ? historySlice.map(metric => new Date(metric.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
    : Array.from({ length: 24 }, (_, i) => `${(i + 1).toString().padStart(2, '0')}:00`);
  // Sanitize: offline windows leave null/NaN in the metrics, which crash
  // ApexCharts' SVG path parser (Sentry HOLLYWOODTRUCKS-API-1).
  const downloadData = historySlice.length > 0
    ? sanitizeSeries(historySlice.map(metric => metric.downloadSpeed))
    : Array(24).fill(0);
  const uploadData = historySlice.length > 0
    ? sanitizeSeries(historySlice.map(metric => metric.uploadSpeed))
    : Array(24).fill(0);
  const pingData = historySlice.length > 0
    ? sanitizeSeries(historySlice.map(metric => metric.pingLatency))
    : Array(24).fill(0);

  // Format uptime
  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // ApexCharts options for speed chart (line)
  const speedChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#3B82F6', '#10B981'],
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { style: { colors: '#9CA3AF', fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '11px' },
        formatter: (val) => `${formatNumber(val)}`,
      },
      title: { text: 'Mbps', style: { color: '#9CA3AF', fontSize: '11px' } },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val) => `${formatNumber(val)} Mbps` },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      labels: { colors: '#9CA3AF' },
    },
    dataLabels: { enabled: false },
  };

  const speedChartSeries = [
    { name: 'Download', data: downloadData },
    { name: 'Upload', data: uploadData },
  ];

  // ApexCharts options for latency chart (area)
  const latencyChartOptions: ApexOptions = {
    chart: {
      type: 'area',
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#F59E0B'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { style: { colors: '#9CA3AF', fontSize: '10px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val) => `${Math.round(val)}`,
      },
      title: { text: 'ms', style: { color: '#9CA3AF', fontSize: '10px' } },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val) => `${Math.round(val)} ms` },
    },
    dataLabels: { enabled: false },
  };

  const latencyChartSeries = [{ name: 'Ping', data: pingData }];

  // ApexCharts options for band distribution (donut)
  const bandChartOptions: ApexOptions = {
    chart: { type: 'donut', background: 'transparent' },
    labels: ['2.4 GHz', '5 GHz'],
    colors: ['#3B82F6', '#10B981'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: { show: false },
        },
      },
    },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { show: false },
    tooltip: { theme: 'dark' },
  };

  const bandChartSeries = [clients2_4GHz, clients5GHz];

  // ApexCharts options for channel distribution (bar)
  const channelChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      background: 'transparent',
      toolbar: { show: false },
    },
    colors: ['#8B5CF6'],
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    plotOptions: {
      bar: { borderRadius: 4, horizontal: false },
    },
    xaxis: {
      categories: channelDistribution.map(c => c.channel),
      labels: {
        style: { colors: '#9CA3AF', fontSize: '9px' },
        rotate: -45,
        rotateAlways: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: '#9CA3AF', fontSize: '10px' } },
    },
    tooltip: { theme: 'dark' },
    dataLabels: { enabled: false },
  };

  const channelChartSeries = [{ name: 'Clients', data: channelDistribution.map(c => c.count) }];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Connection Status Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Satellite className="h-5 w-5" />
            Connection Status
          </CardTitle>
          <Badge variant={connectionStatusVariant(connectionStatus)}>
            <span className="capitalize">{connectionStatus.replace(/_/g, ' ')}</span>
          </Badge>
        </CardHeader>
        <CardContent className="pb-4 md:pb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Signal className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Signal Strength</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold">{measured(signalStrength, 1, ' dBm')}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Signal Quality</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold">{measured(signalQuality, 0, '%')}</p>
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Noise Level</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold">{measured(noiseLevel, 1, ' dBm')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Network Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Network Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pb-4 md:pb-6">
          {/* Current Metrics */}
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            <div className="text-center">
              <ArrowDown className="h-4 w-4 md:h-5 md:w-5 mx-auto text-muted-foreground mb-1 md:mb-2" />
              <p className="text-lg md:text-3xl font-bold">{measured(currentMetrics.downloadSpeed)}</p>
              <p className="text-xs md:text-sm mt-0.5">Mbps</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">Download</p>
            </div>
            <div className="text-center">
              <ArrowUp className="h-4 w-4 md:h-5 md:w-5 mx-auto text-muted-foreground mb-1 md:mb-2" />
              <p className="text-lg md:text-3xl font-bold">{measured(currentMetrics.uploadSpeed)}</p>
              <p className="text-xs md:text-sm mt-0.5">Mbps</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">Upload</p>
            </div>
            <div className="text-center">
              <Zap className="h-4 w-4 md:h-5 md:w-5 mx-auto text-muted-foreground mb-1 md:mb-2" />
              <p className="text-lg md:text-3xl font-bold">{measured(currentMetrics.pingLatency, 0)}</p>
              <p className="text-xs md:text-sm mt-0.5">ms</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">Ping</p>
            </div>
            <div className="text-center">
              <TrendingDown className="h-4 w-4 md:h-5 md:w-5 mx-auto text-muted-foreground mb-1 md:mb-2" />
              <p className="text-lg md:text-3xl font-bold">{measured(currentMetrics.packetLoss)}</p>
              <p className="text-xs md:text-sm mt-0.5">%</p>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">Loss</p>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            {/* Speed History Chart */}
            <div className="h-[120px]">
              <Chart
                type="line"
                options={speedChartOptions}
                series={speedChartSeries}
                height={120}
              />
            </div>

            {/* Latency Chart */}
            <div className="h-[80px] mt-2">
              <Chart
                type="area"
                options={latencyChartOptions}
                series={latencyChartSeries}
                height={80}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WiFi Clients Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            WiFi Clients
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pb-4 md:pb-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <Wifi className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
              <p className="text-3xl md:text-4xl font-bold">{totalClients}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Clients</p>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">2.4</div>
              <p className="text-2xl md:text-3xl font-bold text-blue-500">{clients2_4GHz}</p>
              <p className="text-xs text-muted-foreground mt-1">2.4 GHz</p>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">5</div>
              <p className="text-2xl md:text-3xl font-bold text-green-500">{clients5GHz}</p>
              <p className="text-xs text-muted-foreground mt-1">5 GHz</p>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            {/* Band Distribution & Channel Usage Charts */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-sm font-medium mb-2">Band Distribution</p>
                <div className="h-[100px]">
                  <Chart
                    type="donut"
                    options={bandChartOptions}
                    series={bandChartSeries}
                    height={100}
                  />
                </div>
              </div>

              {/* Channel Distribution Bar Chart */}
              <div>
                <p className="text-sm font-medium mb-2">Channel Usage</p>
                <div className="h-[100px]">
                  <Chart
                    type="bar"
                    options={channelChartOptions}
                    series={channelChartSeries}
                    height={100}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* WiFi Configuration */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                SSID
                <InfoTooltip content="The Wi-Fi network name and password are both the unit code — e.g. 2RE016TCA." side="right" />
              </span>
              <span className="font-medium font-mono">{ssid}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">2.4 GHz Channel</span>
              <span className="font-medium">{system.channel2_4GHz ?? 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">5 GHz Channel</span>
              <span className="font-medium">{system.channel5GHz ?? 'N/A'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hardware Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Hardware Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pb-4 md:pb-6">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Terminal ID</p>
              <p className="text-sm font-mono">{terminalId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Uptime</p>
              <p className="text-sm font-medium">{uptime == null ? 'N/A' : formatUptime(uptime)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Firmware Version</p>
              <p className="text-sm font-mono">{firmwareVersion}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Hardware Version</p>
              <p className="text-sm font-mono">{hardwareVersion}</p>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Last Reboot</p>
              <p className="text-sm">
                {system.lastReboot ? new Date(system.lastReboot).toLocaleString() : 'Unknown'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReboot}
                disabled={rebootState.loading}
                className="h-8 text-xs"
              >
                {rebootState.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Rebooting...
                  </>
                ) : (
                  <>
                    <Router className="h-4 w-4 mr-2" />
                    Reboot Terminal
                  </>
                )}
              </Button>
              {rebootState.success && (
                <div className="flex items-center gap-1 text-xs text-green-500">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>Reboot initiated</span>
                </div>
              )}
              {rebootState.error && (
                <div className="flex items-center gap-1 text-xs text-red-500 max-w-[200px]">
                  <AlertCircle className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{rebootState.error}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}